import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import type { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import type { ViewManager } from '~/engine/view/ViewManager';

/** The views a focus on one image moves, and the Environment that moves them. */
export interface FocusViews {
    environment: Environment;
    /** Every view of the Environment, the one whose fit leads first. */
    views: readonly PlaneView[];
}

/**
 * The views that show the image series `seriesUid`: every view of its
 * resident Environment, in any row and plane, resolved fresh from the
 * registry so a re-minted Environment is never stale. A transversal view
 * leads when there is one, else the first view: the lead's fit sets the
 * scale every view takes, and a box focus snaps the crosshair through the
 * lead's plane. Undefined when there is no view manager or series, the
 * series is not resident, or no view shows it.
 */
export function focusViewsOf(
    registry: DatasetEnvironmentRegistry,
    viewManager: ViewManager | null,
    seriesUid: SeriesInstanceUid | undefined
): FocusViews | undefined {
    if (!viewManager || seriesUid === undefined) {
        return undefined;
    }
    const environment = registry.environmentFor(seriesUid);
    if (!environment) {
        return undefined;
    }
    const views = viewManager.viewsOf(environment);
    const lead =
        views.find(
            (view) => view.label === Orientations.TRANSVERSAL_ORIENTATION
        ) ?? views[0];
    if (!lead) {
        return undefined;
    }
    return {
        environment,
        views: [lead, ...views.filter((view) => view !== lead)],
    };
}
